import { cloneDeep, get, has, mergeWith } from "lodash-es";
import type { FieldDefinition, PersonConfig } from "~/models/Automation";
import type { MaybeRefOrGetter, Ref } from "vue";
import { object } from "yup";
import { ApiError } from "~/plugins/errors/apiError";
import type { Notification } from "#ui/types";
import { v4 as uuidv7, validate, type Version4Options } from "uuid";
import type { ApiResponse } from "~/types/ApiResponse";
import type { LengthAwarePaginator } from "~/types/LengthAwarePaginator";

export function isBlankValue(value: any): boolean {
    if (value === null || value === undefined || value === "") {
        return true;
    }

    if (Array.isArray(value) && value.length === 0) {
        return true;
    }

    if (typeof value === "object" && Object.keys(value).length === 0) {
        return true;
    }

    if (typeof value === "number" && isNaN(value)) {
        return true;
    }

    return false;
}

export function createApiResponse<T = any | null>(
    data: T | null = null,
    options: Partial<Omit<ApiResponse<T>, "data">> = {},
): ApiResponse<T> {
    return {
        message: undefined,
        statusCode: 200,
        statusText: "OK",
        success: true,
        ...options,
        data: data as T,
    };
}

export function createPaginatedResponse<T = any>(
    data: T[] = [],
    options: Partial<Omit<ApiResponse<LengthAwarePaginator<T>>, "data">> = {},
): ApiResponse<LengthAwarePaginator<T>> {
    return createApiResponse(
        {
            current_page: 1,
            first_page_url: "",
            from: 1,
            last_page: 1,
            last_page_url: "",
            links: undefined,
            next_page_url: "",
            path: "",
            per_page: 1,
            prev_page_url: null,
            to: 0,
            total: 0,
            data: data as T[],
        },
        options,
    );
}

export function isFilledValue(value: any): boolean {
    return !isBlankValue(value);
}

export function makeVariable(
    text: string | null | undefined,
    prefix: string = "{{",
    suffix: string = "}}",
) {
    if (!text) {
        return "";
    }

    // Trim the prefix and suffix from the text initially
    text = text
        .toString()
        .trim()
        .replace(new RegExp(`^${prefix}`), "")
        .replace(new RegExp(`${suffix}$`), "");

    return `${prefix}${text}${suffix}`;
}

export function trimPeriods(str: string): string {
    return str.replace(/^\.+|\.+$/g, "");
}

export function keyPrefix(prefix: string, key: string): string {
    const trimmedPrefix = trimPeriods(prefix);
    const trimmedKey = trimPeriods(key);

    // Join the trimmed parts with a single dot
    const joined = [trimmedPrefix, trimmedKey].join(".");

    // Trim potential leading/trailing dots resulting from joining empty trimmed parts
    return trimPeriods(joined);
}

export function isValidUuid(uuid: string) {
    return validate(uuid);
}

export function dateFormatOptions() {
    return Intl.DateTimeFormat().resolvedOptions();
}

export function apiRoute(
    name: string,
    expected: string,
    params?: Record<string, any>,
) {
    return params && Object.entries(params)
        ? expected.replace(/{(\w+)}/g, (_, key) => params[key] || "")
        : expected;
}

export function period(type: string, count: number) {
    return [
        { value: "day", label: count > 1 ? "days" : "day" },
        { value: "week", label: count > 1 ? "weeks" : "week" },
        { value: "month", label: count > 1 ? "months" : "month" },
        { value: "quarter", label: count > 1 ? "quarters" : "quarter" },
        { value: "year", label: count > 1 ? "years" : "year" },
    ].find(({ value }) => value === type)?.label;
}

export function convertToShortNumber(
    number: string | number,
    locale = "en-US",
) {
    const tiers = { k: 1000, m: 1000000, b: 1000000000 };

    if (typeof number === "string") {
        number = number.replaceAll(",", "");
        number = number.includes(".") ? parseFloat(number) : parseInt(number);
    }

    if (!number) {
        return number;
    }

    let tier = Object.keys(tiers)
        .reverse()
        .find(
            (tier) => (number as number) >= tiers[tier as keyof typeof tiers],
        );
    if (!tier) return new Intl.NumberFormat(locale).format(number as number);

    const value = (number as number) / tiers[tier as keyof typeof tiers];

    return (
        new Intl.NumberFormat(locale, { maximumSignificantDigits: 3 }).format(
            value,
        ) + tier.toUpperCase()
    );
}

export function beforeAfter(beforeAfter: string) {
    return [
        { label: "before", value: "last" },
        { label: "after", value: "next" },
    ].find(({ value }) => value === beforeAfter)?.label;
}

export function dateFormatOption(
    key: keyof Intl.ResolvedDateTimeFormatOptions,
) {
    return Intl.DateTimeFormat().resolvedOptions()[
        key
    ] as Intl.ResolvedDateTimeFormatOptions[typeof key];
}

export function createDottedObject(object: any) {
    return Object.keys(object ?? {}).reduce((acc, key) => {
        if (object[key] && typeof object[key] === "object") {
            const flattenedObject = createDottedObject(object[key]);
            Object.keys(flattenedObject).forEach((flattenedKey) => {
                acc[`${key}.${flattenedKey}`] = flattenedObject[flattenedKey];
            });
        } else {
            acc[key] = object[key];
        }
        return acc;
    }, {});
}

export function createUndottedObject(
    object: Record<string | number, any>,
): Record<string | number, any> {
    object = cloneDeep(object ?? {});
    const result: Record<string, any> = {};
    const store: Record<string, any> = {};

    for (let key in object) {
        key.split(".").reduce(
            (
                result: Record<string | number, any>,
                currentValue,
                currentIndex,
                arr,
            ) => {
                if (typeof result !== "object") {
                    store[key] = object[key];

                    return result;
                }

                result[currentValue] =
                    arr.length - 1 === currentIndex
                        ? object[key]
                        : result[currentValue] || {};

                return result[currentValue];
            },
            result,
        );
    }

    return Object.keys(store).length
        ? mergeWith(
              result,
              createUndottedObject(store),
              function (value, srcValue, key) {
                  if (
                      typeof value !== "object" &&
                      typeof srcValue === "object"
                  ) {
                      if (
                          Array.isArray(srcValue) &&
                          !srcValue.includes(value)
                      ) {
                          srcValue.push(value);
                          return srcValue;
                      }

                      return {
                          [key]: value,
                          ...srcValue,
                      };
                  }

                  return srcValue;
              },
          )
        : result;
}

export function range(low: number, high: number, step: number = 1) {
    const matrix = [];
    let initialValue;
    let finalValue;

    const walker = step || 1;
    let chars = false;
    if (!isNaN(low) && !isNaN(high)) {
        initialValue = low;
        finalValue = high;
    } else if (isNaN(low) && isNaN(high)) {
        chars = true;
        initialValue = low.toString().charCodeAt(0);
        finalValue = high.toString().charCodeAt(0);
    } else {
        initialValue = isNaN(low) ? 0 : low;
        finalValue = isNaN(high) ? 0 : high;
    }
    const plus = !(initialValue > finalValue);
    if (plus) {
        while (initialValue <= finalValue) {
            matrix.push(
                chars ? String.fromCharCode(initialValue) : initialValue,
            );
            initialValue += walker;
        }
    } else {
        while (initialValue >= finalValue) {
            matrix.push(
                chars ? String.fromCharCode(initialValue) : initialValue,
            );
            initialValue -= walker;
        }
    }
    return matrix;
}

export {
    FieldCollection,
    FieldMapType,
    createFieldCollection,
    createSimpleFieldCollection,
} from "./fields/collection";
export type { SimpleFieldDefinition } from "./fields/collection";

type UiAvatarParams = {
    size?: number;
    fontSize?: number;
    length?: number;
    name?: string;
    rounded?: boolean;
    bold?: boolean;
    uppercase?: boolean;
    format?: "png" | "svg";
    background?: string;
    color?: string;
};

const createUiAvatar = (params: UiAvatarParams = {}): string => {
    function removeHash(value?: string): string {
        return value?.replace("#", "") || "";
    }

    const size = params.size && Math.min(Math.max(16, params.size), 512);
    const fontSize =
        params.fontSize &&
        Math.round(Math.min(Math.max(0.1, params.fontSize), 1));
    const length = params.length && Math.min(Math.max(1, params.length), 4);
    const name = params.name;
    const rounded = params.rounded;
    const bold = params.bold;
    const uppercase = params.uppercase;
    const format = params.format;
    const background = params.background && removeHash(params.background);
    const color = params.color && removeHash(params.color);

    const queryParams: UiAvatarParams = {
        size,
        fontSize,
        length,
        name,
        rounded,
        bold,
        uppercase,
        format,
        background,
        color,
    };

    const url = new URL("https://ui-avatars.com/api/");

    Object.entries(queryParams).forEach(([key, value]) => {
        if (value !== undefined) {
            url.searchParams.append(key, String(value));
        }
    });

    return url.toString();
};

export function useChanges<T extends Record<string, any>>(
    value: MaybeRefOrGetter<T>,
) {
    const state: Ref<T> = toRef(value) as unknown as Ref<T>;
    const hasChanges = ref<boolean>(false);
    const changes = ref<Partial<T>>({});
    const initial = ref(cloneDeep(state.value));

    function getChangedValues() {
        const changedValues: any = {};

        for (const key in initial.value) {
            if (initial.value[key] !== state.value[key]) {
                changedValues[key] = state.value[key];
            }
        }

        return changedValues;
    }

    watch(
        state,
        () => {
            const changedValues = getChangedValues();

            if (Object.keys(changedValues).length > 0) {
                hasChanges.value = true;
            } else {
                hasChanges.value = false;
            }

            changes.value = changedValues;
        },
        {
            deep: true,
        },
    );

    return {
        initial,
        state,
        changes,
        hasChanges,
        reset: () => {
            state.value = cloneDeep(initial.value) as T;
        },
    };
}

interface HireDataForm<T> {
    toast?: Partial<Notification> | ((state: T) => Partial<Notification>);
    spec: Parameters<typeof object>[0];
    state: MaybeRefOrGetter<T>;
    handleSubmit: () => Promise<any>;
    onCommit?: () => void;
    onReset?: () => void;
    handleError?: (error: ApiError<T> | Error) => void;
}

export function useHireDataForm<T>(options: HireDataForm<T>): {
    state: Ref<T>;
    schema: ReturnType<typeof object>;
    submitting: Ref<boolean>;
    errors: Ref<ApiError | undefined>;
    handleSubmit: () => Promise<void>;
} {
    const initialState = toRef(options.state) as Ref<T>;
    const innerState = toRef(cloneDeep(initialState.value)) as Ref<T>;
    const resetting = ref<boolean>(false);
    const submitting = ref<boolean>(false);
    const errors = ref<ApiError<T> | undefined>();

    function commit() {
        initialState.value = cloneDeep(innerState.value);

        if (options.onCommit) options.onCommit();
    }

    function reset() {
        resetting.value = true;
        innerState.value = cloneDeep(initialState.value);

        if (options.onReset) options.onReset();
    }

    async function handleSubmit() {
        submitting.value = true;
        errors.value = undefined;

        await options
            .handleSubmit()
            .then(() => {
                if (!resetting.value && options.toast)
                    if (typeof options.toast === "function") {
                        const toast = options.toast(innerState.value);
                        useToast().add({
                            ...toast,
                            actions: toast.actions ?? [
                                {
                                    label: "Undo",
                                    click: () => {
                                        reset();
                                        handleSubmit();
                                    },
                                },
                            ],
                            callback: () => {
                                if (toast.callback) toast.callback();

                                commit();
                            },
                        });
                    } else {
                        useToast().add({
                            ...options.toast,
                            actions: options.toast.actions ?? [
                                {
                                    label: "Undo",
                                    click: () => {
                                        reset();
                                        handleSubmit();
                                    },
                                },
                            ],
                            callback: () => {
                                if (options.toast.callback)
                                    options.toast.callback();

                                commit();
                            },
                        });
                    }
            })
            .catch((error: ApiError<T> | Error) => {
                if (error instanceof ApiError) {
                    errors.value = error;
                }

                if (options.handleError)
                    options.handleError(error as ApiError<T> | Error);
            })
            .finally(() => {
                submitting.value = false;
                resetting.value = false;
            });
    }

    return {
        state: innerState as Ref<T>,
        schema: object(options.spec),
        submitting,
        errors,
        handleSubmit,
    };
}

export function createUuid(
    options?: Version4Options,
    buf?: undefined,
    offset?: number,
): string {
    return uuidv7(options, buf, offset);
}

export function route(expected: string, params?: Record<string, any>) {
    return expected.replace(/{(\w+)}/g, (_, key) => params?.[key] || "");
}

export function createPersonConfig(
    mergeObject?: Partial<PersonConfig>,
): PersonConfig {
    let config = {
        uuid: createUuid(),
        title: null,
        initials: null,
        first_name: null,
        label: null,
        prefix: null,
        last_name: null,
        primary_email: null,
        primary_phone_number: null,
        language: null,
        user: null,
        name: null,
        birth_date: null,
        gdpr_expires_at: null,
        is_subscribed: true,
        address: {
            address1: null,
            address2: null,
            city: null,
            province: null,
            zip: null,
        },
        utm_campaign: {
            source: null,
            medium: null,
            campaign: null,
            term: null,
            content: null,
        },
        source: {
            auto_sync: true,
            app: null,
            domain: null,
            url: null,
            external_id: null,
            external_type: null,
        },
        candidate: {
            availability: null,
            availability_date: null,
            employment_type: null,
            notice_period_type: null,
            notice_period_count: null,
            notice_period_end_date: null,
            salary_expectation: null,
            application_source: null,
            application_status: null,
            preferred_contact_method: null,
            willing_to_relocate: null,
            remote_work_preference: null,
            skills: [undefined],
            certificates: [{ name: undefined, url: undefined }],
            auto_sync_skills: true,
            auto_sync_certificates: true,
        },
        tags: [],
        custom_fields: [],
        language_config: {
            code: null,
            name: null,
            ISO_639_1: null,
            ISO_639_2T: null,
            ISO_639_2B: null,
            scope: null,
            type: null,
            additional: null,
        },
        locale: {
            code: null,
            name: null,
        },
        country: {
            code: null,
            name: null,
            alpha_2: null,
            alpha_3: null,
            iso_3166_2: null,
            region: null,
            sub_region: null,
            intermediate_region: null,
            region_code: null,
            sub_region_code: null,
            intermediate_region_code: null,
            additional: null,
        },
    } as PersonConfig;

    if (mergeObject) {
        const dottedConfig = createDottedObject(config);
        const dottedMergeObject = createDottedObject(mergeObject);
        config = createUndottedObject(
            Object.assign(dottedConfig, dottedMergeObject),
        ) as PersonConfig;
    }

    return config;
}

function useAppLogoUrl(options?: {
    type?: "icon" | "text-horizontal" | "text-vertical" | "text";
    grayscale?: boolean;
    extension?: "png" | "svg";
}) {
    const type = options?.type ?? "text-horizontal";
    const grayscale = options?.grayscale ?? false;
    const extension = options?.extension ?? "svg";
    let variant;

    if (type === "icon") {
        variant = grayscale ? "logo_gray" : "logo_color";
    } else if (type === "text-horizontal") {
        variant = grayscale
            ? "logo_text_gray_horizontal"
            : "logo_text_color_horizontal";
    } else if (type === "text-vertical") {
        variant = grayscale
            ? "logo_text_gray_vertical"
            : "logo_text_color_vertical";
    } else {
        variant = "text_gray";
    }

    return `/assets/logo/${extension}/${variant}.${extension}`;
}

export { createUiAvatar, useAppLogoUrl };
